// frontend/src/pages/builder/savedChart.js
// Phase 3 step 9: the builder's state <-> a saved chart. Pure and unit-tested.
//
// Saving sends what the /charts API stores for the new builder: the spec
// (the query, exactly as the preview ran it), the chart type, the title and
// the presentation (how it looks). Loading reads those back into the
// builder's own state -- the draft, the chosen type, the data settings, the
// format, a table's column order and widths, a matrix's layout, the filters
// -- so a chart opens exactly as it was left. Anything the builder worked
// out on its own (an automatic title, the automatic sort) comes back as
// automatic, so it keeps following the fields.
import { CHART_TYPES } from './chartTypes';
import { DEFAULT_LIMIT, autoSort, emptySettings } from './dataSettings';
import { emptyFormat } from './formatSettings';
import { emptyMatrix } from './matrixSlots';
import { fromSpecFilters } from './filters';
import { columnWidths, sameTarget, targetKey } from '../../charts/tableOrder';
import { normalizePresentation } from '../../charts/format';

const TYPE_IDS = CHART_TYPES.map((t) => t.id);

/**
 * The POST / PUT body for the chart as the builder shows it. Visibility is
 * never sent: an update keeps the chart's, a new chart starts private.
 * `folderId` (Save as copy, step 10c) puts a new chart in that folder.
 */
export function toSavePayload({ dataset, spec, type, title, presentation, folderId = null }) {
  const body = {
    connector_config_id: dataset.connector_config_id,
    entity: dataset.entity || 'issues',
    title,
    chart_type: type,
    spec,
    // null would mean "keep what is stored" on an update; {} means "all defaults".
    presentation: presentation || {},
  };
  if (Number.isInteger(folderId)) body.folder_id = folderId;
  return body;
}

/** A copy's title: "(Copy)" added when it would otherwise be the same as the original's. */
export function copyTitle(title, originalTitle) {
  const t = String(title || '').trim();
  return t && t === String(originalTitle || '').trim() ? `${t} (Copy)`.slice(0, 120) : t;
}

/** True when the chart was made by this builder (it has a spec); legacy charts are converted in step 11. */
export const isBuilderChart = (chart) => Boolean(chart && chart.spec && typeof chart.spec === 'object' && chart.dataset_id);

/**
 * Step 10: how a saved builder chart is drawn anywhere in the app -- its own
 * type, presentation and (for a matrix) layout, for ChartView with chartId.
 * An unknown stored type falls back to the table, which shows any result.
 */
export function viewProps(chart) {
  const type = TYPE_IDS.includes(chart && chart.chart_type) ? chart.chart_type : 'table';
  const matrix = type === 'matrix' && chart.spec && chart.spec.matrix ? chart.spec.matrix : null;
  return { type: type === 'matrix' && !matrix ? 'table' : type, presentation: chart.presentation || null, matrix };
}

/**
 * Where Edit goes: the builder for its charts; for a chart from the previous
 * builder (step 12 removed it), its own page, where it can be updated (step 11).
 */
export function editPath(chart) {
  return isBuilderChart(chart) ? `/charts/build?chart=${chart.id}` : `/charts/${chart.id}`;
}

const item = (d) => (d.grain ? { field: d.field, grain: d.grain } : { field: d.field });
const measure = (m) => (m.field ? { aggregation: m.aggregation, field: m.field } : { aggregation: m.aggregation });

/**
 * A saved chart -> the builder's state: { draft, chosenType, settings,
 * format, order, widths, mx, filters }. `describe(draft)` is the builder's
 * automatic title, so a title equal to it is left automatic.
 */
export function fromSavedChart(chart, fieldsByKey = {}, describe = () => '') {
  const spec = chart.spec || {};
  const draft = {
    dimensions: (spec.dimensions || []).map(item),
    measures: (spec.measures || []).map(measure),
  };
  // No value chosen is stored as a bare count; the builder shows that as "Count of records" with no chip.
  if (draft.measures.length === 1 && draft.measures[0].aggregation === 'count' && !draft.measures[0].field) draft.measures = [];

  const chosenType = TYPE_IDS.includes(chart.chart_type) ? chart.chart_type : null;
  const mx = spec.matrix ? { columns: Number(spec.matrix.columns) || 0, totals: spec.matrix.totals !== false } : emptyMatrix();

  const settings = emptySettings();
  const first = Array.isArray(spec.sort) && spec.sort[0];
  if (first && first.by == null && (first.field || first.aggregation)) {
    const target = first.aggregation ? measure(first) : { field: first.field };
    const chosen = { target, dir: first.dir === 'desc' ? 'desc' : 'asc' };
    // For a chart, the automatic sort was stored too; keep it automatic so it follows the fields.
    const auto = chosenType === 'matrix' ? null : autoSort({ dimensions: draft.dimensions, measures: draft.measures.length ? draft.measures : [{ aggregation: 'count' }] });
    const isAuto = auto && sameTarget(auto.target, chosen.target) && auto.dir === chosen.dir;
    settings.sort = isAuto ? null : chosen;
  }
  if (Number.isInteger(spec.limit) && spec.limit !== DEFAULT_LIMIT) settings.limit = spec.limit;
  if (typeof spec.empty_periods === 'string') settings.emptyPeriods = spec.empty_periods;

  const p = normalizePresentation(chart.presentation);
  const format = {
    ...emptyFormat(),
    title: chart.title && chart.title !== describe(draft) ? chart.title : '',
    number_format: p.number_format, currency: p.currency, decimals: p.decimals, value_labels: p.value_labels,
  };
  const columns = chart.presentation && Array.isArray(chart.presentation.columns) ? chart.presentation.columns : [];
  const order = columns.map((c) => targetKey(c)).filter(Boolean);
  const widths = columnWidths(columns);
  const filters = fromSpecFilters(spec.filters, fieldsByKey);

  return { draft, chosenType, settings, format, order, widths, mx, filters };
}

/** A readable reason a copy failed (step 10c). */
export function copyFailureMessage(err) {
  if (err && err.code === 'FORBIDDEN') return 'Only admins and analysts can copy charts.';
  if (err && err.code === 'LEGACY_CHART') return 'Update this chart to the new builder first; a copy carries only the new query.';
  if (err && err.status === 402) return 'Your plan has reached its chart limit.';
  if (err && err.code === 'NOT_FOUND') return 'This chart could not be found. It may have been deleted.';
  return "The chart couldn't be copied. Try again.";
}

/** A readable reason a save failed, from the API's typed error. */
export function saveFailureMessage(err) {
  const code = err && err.code;
  if (code === 'FORBIDDEN') return 'Only admins and analysts can save charts.';
  if (code === 'INVALID_SPEC' || code === 'INVALID_QUERY') return `This chart can't be saved as it is: ${err.message}`;
  if (code === 'INVALID_PRESENTATION') return `A format setting was refused: ${err.message}`;
  if (err && err.status === 402) return 'Your plan has reached its chart limit.';
  if (code === 'NOT_FOUND') return 'This chart could not be found. It may have been deleted.';
  return "The chart couldn't be saved. Try again.";
}
